import { useEffect, useState, type MouseEvent } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { Button, LockIcon, ShieldCheckIcon, Spinner } from '@k1p4/ui'
import { api, localSignIn, portalSignInUrl, type AuthEntryResponse } from '../../api/client'
import { useAuth } from '../../auth/AuthProvider'
import { withLang } from './portalLinks'
import styles from './Login.module.css'

/**
 * Powody z `?portal_error=`, które ekran umie nazwać po imieniu. Resztę
 * (stan przepływu, wymiana kodu) pokazujemy jednym zdaniem „spróbuj ponownie"
 * — człowiek nic więcej z nimi nie zrobi.
 */
const PORTAL_ERROR_KEYS: Record<string, string> = {
  module_not_enabled: 'auth.errorModuleNotEnabled',
  access_requested: 'auth.errorAccessRequested',
  no_account: 'auth.errorNoAccount',
  not_owner: 'auth.errorNotOwner',
  email_taken: 'auth.errorEmailTaken',
  slug_taken: 'auth.errorSlugTaken',
  tenant_mismatch: 'auth.errorTenantMismatch',
  provider_unreachable: 'auth.errorProviderUnreachable',
  portal_disabled: 'auth.portalUnavailable',
}

/**
 * Ekran logowania (spec 06) po ADR-0057: jedyną drogą wejścia jest portal 33rp.
 *
 * Dwa warianty, oba wyliczone przez SERWER (`GET /auth/entry`), bo to fakty
 * o wdrożeniu i o hoście, nie o koncie:
 *  - host obszaru firmy: jeden przycisk „Zaloguj przez 33rp";
 *  - apeks: przycisku nie ma, bo portal odsyła pod adres OBSZARU, a apeks nie
 *    niesie sluga. Zamiast niego — wskazówka, pod jakim adresem stoi firma,
 *    i droga do portalu, którego kafelek prowadzi wprost pod host obszaru.
 *
 * Hasła, magic linku, dostawców OAuth ani MFA tu nie ma — odeszły z serwerem.
 */
export function Login() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { refresh } = useAuth()
  const [entry, setEntry] = useState<AuthEntryResponse | null>(null)
  const [failed, setFailed] = useState(false)

  const portalError = searchParams.get('portal_error')
  const errorKey = portalError ? (PORTAL_ERROR_KEYS[portalError] ?? 'auth.errorGeneric') : null

  useEffect(() => {
    const controller = new AbortController()
    api
      .authEntry(controller.signal)
      .then(setEntry)
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true)
      })
    return () => {
      controller.abort()
    }
  }, [])

  /*
   * Powrót tam, skąd guard odesłał na logowanie (`state.from`), ale TYLKO
   * ścieżka aplikacji — serwer i tak odrzuca wszystko, co nie jest ścieżką.
   * Bez niej serwer wybiera sam po roli: `/app` dla kadr, `/me` dla pracownika.
   */
  const from = (location.state as { from?: unknown } | null)?.from
  const next = typeof from === 'string' && /^\/(app|me)(\/|$)/.test(from) ? from : null
  const portalHref = portalSignInUrl(next)

  /*
   * Obraz demonstracyjny nie ma portalu, do którego mógłby odesłać, więc
   * wejście dzieje się tu, bez żądania (ADR-0059) — i idzie dalej tą samą
   * drogą co po powrocie z portalu: sesja z `GET /auth/session`, a start
   * (`next` albo rola) wybiera `RedirectIfAuthenticated`, gdy sesja się pojawi.
   * W wersji na żywo `localSignIn()` to `null`, a przycisk jest zwykłym
   * odnośnikiem do `/auth/portal`.
   */
  const signInHere = localSignIn()
  const onPortalClick = signInHere
    ? (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()
        void signInHere(next).then(() => refresh())
      }
    : undefined

  const lang = i18n.language
  const registrationUrl = entry?.registrationUrl ? withLang(entry.registrationUrl, lang) : null
  const portalUrl = entry?.portalUrl ? withLang(entry.portalUrl, lang) : null

  return (
    <>
      <h1 className={styles.heading}>{t('auth.title')}</h1>
      <p className={styles.subtitle}>
        {entry !== null && !entry.tenantHost ? t('auth.subtitleApex') : t('auth.subtitle')}
      </p>

      {errorKey !== null ? (
        <p className={styles.error} role="alert">
          {t(errorKey)}
        </p>
      ) : null}

      {entry === null && !failed ? (
        <p className={styles.center}>
          <Spinner label={t('common.loading')} />
        </p>
      ) : null}

      {/*
        ZWYKŁY `<a>`, nie `<Link>`, i to jest konieczność, nie styl: ta ścieżka
        WYCHODZI Z APLIKACJI. Router SPA przejąłby kliknięcie i próbował
        dopasować `/api/v1/auth/portal` do własnych tras, więc przeglądarka
        nigdy nie wysłałaby żądania, które ma się skończyć przekierowaniem na
        portal.
      */}
      {entry?.portalSignIn ? (
        <a href={portalHref} onClick={onPortalClick}>
          <Button className={styles.ssoButton} size="lg">
            <LockIcon size={17} />
            {t('auth.portalButton')}
          </Button>
        </a>
      ) : null}

      {entry !== null && entry.tenantHost && !entry.portalSignIn ? (
        <p className={styles.notice} role="status">
          {t('auth.portalUnavailable')}
        </p>
      ) : null}

      {entry !== null && !entry.tenantHost ? (
        <>
          <p className={styles.notice}>
            {entry.tenantHostSuffix
              ? (
                  /*
                    Adres apeksu nie łamie się w środku (fala 9, grupa 170) —
                    nowrap zamiast twardego łącznika, bo skopiowany adres musi działać.
                  */
                  <Trans
                    i18nKey="auth.apexBody"
                    values={{ host: `${t('auth.apexHostLabel')}.${entry.tenantHostSuffix}` }}
                    components={{ host: <span className={styles.nowrap} /> }}
                  />
                )
              : t('auth.apexBodyNoHost')}
          </p>
          {portalUrl ? (
            <a href={portalUrl}>
              <Button className={styles.ssoButton} size="lg" variant="dark">
                {t('auth.openPortal')}
              </Button>
            </a>
          ) : (
            <p className={styles.notice} role="status">
              {t('auth.portalUnavailable')}
            </p>
          )}
        </>
      ) : null}

      {failed ? (
        <p className={styles.error} role="alert">
          {t('auth.errorGeneric')}
        </p>
      ) : null}

      <p className={styles.trustNote}>
        <span className={styles.trustIcon} aria-hidden="true">
          <ShieldCheckIcon size={14} />
        </span>
        {t('auth.trustNote')}
      </p>

      {/* Podręcznik jest trasą SPA dostępną bez logowania (ADR-0062), więc `Link`, nie `<a>`. */}
      <p className={styles.footerPrompt}>
        <Link to="/docs" className={styles.footerLink}>
          {t('auth.help')}
        </Link>
      </p>

      {registrationUrl ? (
        <p className={styles.footerPrompt}>
          {t('auth.noAccount')}{' '}
          <a href={registrationUrl} className={styles.footerLink}>
            {t('auth.createOrganisation')}
          </a>
        </p>
      ) : null}
    </>
  )
}
